import { Empty, IconFont, Input, Popover, usePopoverHeight } from '@byecode/ui'
import { getBlockIcon } from '@lighthouse/block'
import type { NodeIdWithScope, WithBlockOption } from '@lighthouse/shared'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { useCurrentBreakPointId, useMainBreakPoint } from '@/hooks/useApplication'

interface ComponentSelectorProps {
    value: string
    onChange: (value: NodeIdWithScope) => void
    onHover?: (value?: NodeIdWithScope) => void
    options: WithBlockOption[]
}

interface ComponentItemProps {
    value?: string
    option: WithBlockOption
    onChange?: (value: NodeIdWithScope) => void
    onHover?: (value?: NodeIdWithScope) => void
}

const SCxComponentSelectorWrapper = styled.div``

const SCxComponentSelectorSearch = styled.div`
    padding: 4px 12px 0;
    margin-bottom: 8px;
`

const SCxComponentSelectorPreviewer = styled.div`
    display: flex;
    height: 32px;
    align-items: center;
    padding: 0 6px 0 8px;
    border-radius: 6px;
    background-color: var(--color-gray-100);

    &:hover {
        background-color: var(--color-gray-200);
    }
`

const SCxPlaceholder = styled.span`
    flex: 1;
    color: var(--color-gray-400);
`

const SCxNotFound = styled.span`
    margin-right: auto;
    padding: 0px 4px;
    line-height: 22px;
    color: var(--color-red-500);
    background-color: var(--color-red-200);
    border-radius: 4px;
    font-size: 14px;
`

const SCxComponentSelectorPreviewerLabel = styled.span`
    flex: 1;
    margin-right: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--color-gray-800);
    font-size: var(---font-size-sm);
`

const SCxComponentSelectorItem = styled.div`
    position: relative;
    user-select: none;
`

const SCxComponentSelectorItemContent = styled.div`
    display: flex;
    align-items: center;
    height: 32px;
    cursor: pointer;
    padding: 0 16px;

    &:hover {
        background-color: var(--color-gray-100);
    }
`

const SCxComponentSelectorItemLabel = styled.span`
    flex: 1;
    margin-right: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--color-gray-800);
    font-size: var(---font-size-sm);
`

const SCxComponentSelectorItemIconWrapper = styled.div`
    display: flex;
    width: 20px;
    justify-content: center;
    align-items: center;
`

const ComponentItem: React.FC<ComponentItemProps> = ({ value, option, onChange, onHover }) => {
    const breakPointId = useCurrentBreakPointId()
    const mainBreakPoint = useMainBreakPoint()
    const [open, setOpen] = useState(true)
    const ref = useRef<HTMLDivElement>(null)
    const { children, label, value: optionValue, block, level } = option
    const hasChildren = children && children.length > 0
    const isChecked = value === optionValue.id

    useEffect(() => {
        const mouseEnter = () => onHover?.(optionValue)
        const mouseLeave = () => onHover?.(undefined)
        const dom = ref.current
        if (dom) {
            dom.addEventListener('mouseenter', mouseEnter)
            dom.addEventListener('mouseleave', mouseLeave)
        }

        return () => {
            if (dom) {
                dom.removeEventListener('mouseenter', mouseEnter)
                dom.removeEventListener('mouseleave', mouseLeave)
            }
        }
    }, [onHover, optionValue])

    const itemContentStyle = useMemo(() => {
        return { paddingLeft: (level || 0) * 24 + 8 }
    }, [level])

    return (
        <SCxComponentSelectorItem>
            <SCxComponentSelectorItemContent style={itemContentStyle} ref={ref} onClick={() => onChange?.(optionValue)}>
                {hasChildren ? (
                    <SCxComponentSelectorItemIconWrapper
                        onClick={ev => {
                            ev.stopPropagation()
                            setOpen(o => !o)
                        }}
                    >
                        <IconFont type={open ? 'CaretDown' : 'CaretUp'} size={16} color="var(--color-gray-400)" />
                    </SCxComponentSelectorItemIconWrapper>
                ) : (
                    <SCxComponentSelectorItemIconWrapper />
                )}
                <IconFont
                    type={getBlockIcon(block, breakPointId, mainBreakPoint)}
                    size={16}
                    color="var(--color-gray-400)"
                    style={{ marginRight: 6 }}
                />
                <SCxComponentSelectorItemLabel>{label}</SCxComponentSelectorItemLabel>
                {isChecked && <IconFont type="Tick" color="var(--color-main)" />}
            </SCxComponentSelectorItemContent>
        </SCxComponentSelectorItem>
    )
}

export const ComponentSelector: React.FC<ComponentSelectorProps> = ({ value, onChange, options, onHover }) => {
    const breakPointId = useCurrentBreakPointId()
    const mainBreakPoint = useMainBreakPoint()
    const [opened, setOpened] = useState(false)

    // 关闭选择框时，清除hover态
    useUpdateEffect(() => {
        if (!opened) {
            onHover?.()
        }
    }, [opened])

    // 修复 usePopoverHeight 参数类型错误，传递对象而不是布尔值
    const { height: maxHeight, ref } = usePopoverHeight({ opened })
    const currentOption = useMemo(() => {
        const recursive = (options: WithBlockOption[]): WithBlockOption | undefined => {
            for (const option of options) {
                if (option.value.id === value) {
                    return option
                }

                if (option.children && option.children.length > 0) {
                    const result = recursive(option.children)

                    if (result) {
                        return result
                    }
                }
            }
        }
        return recursive(options)
    }, [options, value])

    const [searchValue, setSearchValue] = useState('')

    const flattenOptions = useMemo(() => {
        // 通过层序遍历，将所有节点展开
        const stack = options.map(option => ({ ...option, level: 0 }))
        const result: WithBlockOption[] = []
        while (stack.length) {
            const node = stack.shift() as WithBlockOption
            if (!searchValue || node.label.includes(searchValue)) {
                result.push(node)
            }
            if (node.children) {
                stack.unshift(...node.children.map(child => ({ ...child, level: node.level + 1 })))
            }
        }
        return result
    }, [options, searchValue])

    const handleChange = (value: NodeIdWithScope) => {
        onChange(value)
        setOpened(false)
    }

    const targetDom = useMemo(() => {
        if (!value) {
            return <SCxPlaceholder>请选择组件</SCxPlaceholder>
        }

        if (currentOption) {
            return (
                <>
                    <IconFont
                        type={getBlockIcon(currentOption.block, breakPointId, mainBreakPoint)}
                        size={16}
                        style={{ marginRight: 6 }}
                        color="var(--color-gray-400)"
                    />
                    <SCxComponentSelectorPreviewerLabel>{currentOption.label}</SCxComponentSelectorPreviewerLabel>
                </>
            )
        }

        return <SCxNotFound>找不到组件</SCxNotFound>
    }, [currentOption, mainBreakPoint, breakPointId, value])

    return (
        <SCxComponentSelectorWrapper ref={ref}>
            <Popover opened={opened} width={240} position="bottom-end" onChange={setOpened}>
                <Popover.Target>
                    <SCxComponentSelectorPreviewer>
                        {targetDom}
                        <IconFont type={opened ? 'ArrowUpSmall' : 'ArrowDownSmall'} size={16} color="var(--color-gray-400)" />
                    </SCxComponentSelectorPreviewer>
                </Popover.Target>
                <Popover.Dropdown style={{ maxHeight, overflowY: 'auto' }}>
                    <SCxComponentSelectorSearch>
                        <Input
                            value={searchValue}
                            onChange={e => setSearchValue(e.target.value)}
                            prefix={<IconFont type="Search" />}
                            placeholder="搜索"
                        />
                    </SCxComponentSelectorSearch>
                    {flattenOptions.length > 0 ? (
                        <>
                            {flattenOptions.map(option => (
                                <ComponentItem
                                    option={option}
                                    key={`${option.value.scope || ''}@${option.value.id}`}
                                    value={value}
                                    onChange={handleChange}
                                    onHover={onHover}
                                />
                            ))}
                        </>
                    ) : (
                        <Empty styles={{ root: { height: 160 } }} description="未找到组件" />
                    )}
                </Popover.Dropdown>
            </Popover>
        </SCxComponentSelectorWrapper>
    )
}
